<template>
  <div class="relative h-full flex items-center">
    <div class="flex items-center gap-16">
      <!-- 上一个 -->
      <Button color="glass" size="lg" shape="circle" :disabled="!canNavigate" @click="handlePrev">
        <Icon icon="Direction-Wireframe-Left2" />
      </Button>

      <!-- 轮播视口 -->
      <div
        class="perspective-[900px] relative flex items-center justify-center"
        :style="viewportStyle"
      >
        <!-- 占位：当 item 不足时填充空槽 -->
        <div
          v-if="showLeftPlaceholder"
          class="transform-3d absolute w-81.5 h-51.5 glass-frosted"
          :style="{
            transition:
              'transform 0.52s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.52s cubic-bezier(0.2, 0.8, 0.2, 1)',
            willChange: 'transform, opacity',
            ...getPlaceholderStyle(-1),
          }"
        />
        <div
          v-if="showCenterPlaceholder"
          class="transform-3d absolute w-81.5 h-51.5 glass-frosted"
          :style="{
            transition:
              'transform 0.52s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.52s cubic-bezier(0.2, 0.8, 0.2, 1)',
            willChange: 'transform, opacity',
            ...getPlaceholderStyle(0),
          }"
        />
        <div
          v-if="showRightPlaceholder"
          class="transform-3d absolute w-81.5 h-51.5 glass-frosted"
          :style="{
            transition:
              'transform 0.52s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.52s cubic-bezier(0.2, 0.8, 0.2, 1)',
            willChange: 'transform, opacity',
            ...getPlaceholderStyle(1),
          }"
        />

        <!-- 屏显 item：绝对定位 + transform 实现切换动画 -->
        <div
          v-for="item in screenDisplays"
          :key="item.slot"
          class="screen-carousel-reflection transform-3d absolute cursor-grab active:cursor-grabbing"
          :style="{
            transition:
              'transform 0.52s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.52s cubic-bezier(0.2, 0.8, 0.2, 1)',
            willChange: 'transform, opacity',
            ...getItemStyle(item.slot),
          }"
          @mousedown="onMouseDown"
          @touchstart="onTouchStart"
          @click="handleItemClick(item.slot, $event)"
        >
          <div
            class="w-81.5 h-51.5 border-3 rounded-l-l rounded-r-l overflow-hidden"
            :class="
              item.slot === props.page ? 'border-brand-blue1' : 'border-transparent glass-frosted'
            "
            draggable="false"
            @dragstart.prevent
          >
            <img
              v-if="item.thumbnail && !item.error"
              :src="item.thumbnail"
              width="326"
              height="206"
              class="w-full h-full object-cover"
            />
            <div
              v-else-if="item.error"
              class="w-full h-full p-2 flex gap-1 bg-white text-s-medium text-brand-blue1"
            >
              <Icon icon="Multimedia-Fill-Cut" class="size-4" />
              <span>{{ $t('pageAndPlayer.carousel.imgMissing') }}</span>
            </div>
            <div
              v-else
              class="w-full h-full bg-white flex items-center justify-center px-9 text-center text-l-medium text-text-body-default"
            >
              {{ $t('pageAndPlayer.carousel.pendingImg') }}
            </div>
          </div>

          <!-- Info and Action -->
          <div
            v-if="item.slot === props.page"
            class="px-2 absolute bottom-2 flex items-center gap-2 w-full"
          >
            <div class="flex-1">
              <div v-if="item.thumbname" class="p-1.5 rounded-m glass-frosted">
                <span class="text-black text-s-medium">
                  {{
                    item.kind === 'home'
                      ? $t('pageAndPlayer.pages.systemPage.home')
                      : item.kind === 'mouse-rt'
                        ? $t('pageAndPlayer.pages.systemPage.mouseRt')
                        : `${item.thumbname}${item.thumbext}`
                  }}
                </span>
              </div>
            </div>
            <Button
              v-if="canDeleteItem(item.slot) && isEdit"
              color="glass"
              size="icon-sm"
              shape="circle"
              @click.stop="handleDeleteClick(item.slot)"
            >
              <Icon icon="Function-Wireframe-Delete" />
            </Button>
          </div>
        </div>
      </div>

      <!-- 下一个 -->
      <Button color="glass" size="lg" shape="circle" :disabled="!canNavigate" @click="handleNext">
        <Icon icon="Direction-Wireframe-Right2" />
      </Button>
    </div>

    <!-- Indicator -->
    <div
      v-if="screenDisplaysLength > 1"
      class="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
    >
      <div class="flex gap-2">
        <button
          v-for="item in screenDisplays"
          :key="item.slot"
          type="button"
          class="size-2 rounded-full transition-colors cursor-pointer"
          :class="item.slot === props.page ? 'bg-black-white' : 'bg-text-glass-default'"
          @click="handleIndicatorClick(item.slot)"
        />
      </div>
    </div>

    <div class="absolute -right-36 top-1/2 flex flex-col gap-2 -translate-y-1/2">
      <Button v-if="showUpload" color="glass" :disabled="uploadDisabled" @click="handleUpload">
        <Icon icon="Direction-Wireframe-ArrowUp" />
        <span class="mr-2">{{ $t('pageAndPlayer.carousel.upload') }}</span>
      </Button>
      <Button v-if="canCreate" color="glass" :disabled="!canAdd" @click="handleCreate">
        <Icon icon="Function-Wireframe-Add11" />
        <span class="mr-2">{{ $t('pageAndPlayer.carousel.add') }}</span>
      </Button>
    </div>

    <Dialog
      v-model:open="isDeleteDialogOpen"
      :title="$t('pageAndPlayer.carouselDeleteDialog.title')"
      content-class="w-90"
    >
      <div class="py-2 text-m-medium text-text-glass-default">
        {{ $t('pageAndPlayer.carouselDeleteDialog.description') }}
      </div>
      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button color="text-b-w" @click="isDeleteDialogOpen = false">{{
            $t('common.cancel')
          }}</Button>
          <Button color="black" @click="confirmDelete">{{ $t('common.confirmDelete') }}</Button>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
  import { computed, onUnmounted, ref, shallowRef, watch, type CSSProperties } from 'vue'
  import { Button, Dialog, Icon } from '@rapoo/element'
  import type { PageItem } from '../utils/types'

  type NavigationDirection = -1 | 1
  type SlotPosition = -1 | 0 | 1

  // 激活卡片尺寸（侧边卡片通过 scale 缩放得到）
  const ACTIVE_WIDTH = 326
  const ACTIVE_HEIGHT = 206
  const SIDE_SCALE = 0.7
  const GAP = 32

  // 卡片中心之间的水平间距：激活半宽 + 间隙 + 侧边半宽
  const spacing = (ACTIVE_WIDTH * (1 + SIDE_SCALE)) / 2 + GAP
  const sideHalfWidth = (ACTIVE_WIDTH * SIDE_SCALE) / 2

  const VIEWPORT_WIDTH = (spacing + sideHalfWidth) * 2

  const viewportStyle: CSSProperties = {
    width: `${VIEWPORT_WIDTH}px`,
    height: `${ACTIVE_HEIGHT}px`,
  }

  const props = withDefaults(
    defineProps<{
      items: PageItem[]
      page: number
      isEdit?: boolean
      canCreate?: boolean
      showUpload?: boolean
      uploadDisabled?: boolean
    }>(),
    {
      isEdit: true,
      canCreate: false,
      showUpload: true,
      uploadDisabled: true,
    },
  )

  const emit = defineEmits<{
    (e: 'update:page', slot: number): void
    (e: 'create'): void
    (e: 'upload'): void
    (e: 'delete', slot: number): void
  }>()

  const isDeleteDialogOpen = ref(false)
  const deletingSlot = ref(-1)

  const canDeleteItem = (slot: number) =>
    screenDisplays.value.some((item) => item.slot === slot && item.kind === 'custom') &&
    screenDisplaysLength.value > 1

  // 用于显示的屏显列表
  const screenDisplays = computed(() => props.items)
  const screenDisplaysLength = computed(() => screenDisplays.value.length)

  // item 不足时，使用占位填充对应的空槽
  const visibleSideOffset = computed(() => {
    if (screenDisplaysLength.value !== 2) return 0

    const positionIndex = activePositionIndex.value === 0 ? 1 : 0
    return getItemOffsetByPositionIndex(positionIndex)
  })

  // 是否显示左侧屏显占位
  const showLeftPlaceholder = computed(
    () =>
      screenDisplaysLength.value <= 1 ||
      (screenDisplaysLength.value === 2 && visibleSideOffset.value !== -1),
  )
  // 是否显示中间屏显占位
  const showCenterPlaceholder = computed(() => screenDisplaysLength.value === 0)
  // 是否显示右侧屏显占位
  const showRightPlaceholder = computed(
    () =>
      screenDisplaysLength.value <= 1 ||
      (screenDisplaysLength.value === 2 && visibleSideOffset.value !== 1),
  )

  // 当前激活的屏显 index
  const activePositionIndex = computed(() => {
    if (screenDisplaysLength.value === 0) return -1

    const positionIndex = getPositionIndexById(props.page)
    return positionIndex === -1 ? 0 : positionIndex
  })

  const canNavigate = computed(() => screenDisplaysLength.value > 1)
  const canAdd = computed(() => props.canCreate)

  // 循环取索引
  const wrapPositionIndex = (positionIndex: number, length: number) =>
    ((positionIndex % length) + length) % length

  /**
   * 通过 id 获取屏显 index
   * @param id
   */
  const getPositionIndexById = (id: number) =>
    screenDisplays.value.findIndex((item) => item.slot === id)

  const isDragging = shallowRef(false)
  const hasDragged = shallowRef(false)
  const dragOffset = shallowRef(0)
  const startX = shallowRef(0)
  const lastMoveDirection = shallowRef<NavigationDirection>(1)

  // 通过切换的前后方向获取屏显 id
  const getDirectionToItemId = (id: number): NavigationDirection => {
    const len = screenDisplaysLength.value
    if (len <= 1) return 1

    const positionIndex = getPositionIndexById(id)
    if (positionIndex === -1) return 1

    const current = activePositionIndex.value
    const forwardSteps = wrapPositionIndex(positionIndex - current, len)
    const backwardSteps = wrapPositionIndex(current - positionIndex, len)

    return forwardSteps <= backwardSteps ? 1 : -1
  }

  // 通过 index 获取当前切换动画偏移量
  const getItemOffsetByPositionIndex = (positionIndex: number) => {
    const len = screenDisplaysLength.value
    if (len <= 1) return 0

    const rawDiff = positionIndex - activePositionIndex.value

    if (len === 2) return rawDiff

    let offset = rawDiff
    if (offset > len / 2) offset -= len
    if (offset < -len / 2) offset += len

    return offset
  }

  const getVisibleTransform = (offset: number, offsetDrag = dragOffset.value) => {
    const absOffset = Math.abs(offset)
    const translateX = offset * spacing + offsetDrag
    const scale = absOffset === 0 ? 1 : SIDE_SCALE
    const rotateY = offset === 0 ? 0 : offset > 0 ? -38 : 38
    const translateZ = offset === 0 ? 0 : -88

    return `translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`
  }

  const getHiddenTransform = () => {
    const rotateY = lastMoveDirection.value > 0 ? -54 : 54
    return `translateX(0px) translateZ(-160px) rotateY(${rotateY}deg) scale(0.55)`
  }

  const getPlaceholderStyle = (position: SlotPosition): CSSProperties => {
    return {
      transform: position === 0 ? getVisibleTransform(0, 0) : getVisibleTransform(position, 0),
      zIndex: 10 - Math.abs(position),
      pointerEvents: 'none',
    }
  }

  // 计算位于 index 的 item 相对激活项的样式（取最短循环路径）
  const getItemStyle = (id: number): CSSProperties => {
    const len = screenDisplaysLength.value
    if (len === 0) return { display: 'none' }

    const positionIndex = getPositionIndexById(id)
    if (positionIndex === -1) return { display: 'none' }

    const offset = getItemOffsetByPositionIndex(positionIndex)
    const absOffset = Math.abs(offset)

    // 只展示 上一个 / 激活 / 下一个
    if (absOffset > 1) {
      return {
        opacity: 0,
        pointerEvents: 'none',
        zIndex: 0,
        transform: getHiddenTransform(),
      }
    }

    return {
      transform: getVisibleTransform(offset),
      zIndex: 100 - absOffset,
      opacity: 1,
    }
  }

  // 切换屏显
  const navigate = (direction: 1 | -1) => {
    if (!canNavigate.value) return

    lastMoveDirection.value = direction
    const nextPositionIndex = wrapPositionIndex(
      activePositionIndex.value + direction,
      screenDisplaysLength.value,
    )
    const nextItem = screenDisplays.value[nextPositionIndex]

    emit('update:page', nextItem.slot)
  }

  // 切到上一张
  const handlePrev = () => navigate(-1)

  // 切到下一张
  const handleNext = () => navigate(1)

  // 点击指示器
  const handleIndicatorClick = (id: number) => {
    if (id === props.page) return

    lastMoveDirection.value = getDirectionToItemId(id)
    emit('update:page', id)
  }

  // 点击屏显
  const handleItemClick = (id: number, e: MouseEvent) => {
    if (hasDragged.value) {
      e.preventDefault()
      hasDragged.value = false
      return
    }

    if (id === props.page) return

    lastMoveDirection.value = getDirectionToItemId(id)
    emit('update:page', id)
  }

  const handleCreate = () => {
    if (!canAdd.value) return

    emit('create')
  }

  const handleUpload = () => {
    emit('upload')
  }

  const handleDeleteClick = (slot: number) => {
    if (!canDeleteItem(slot)) return

    deletingSlot.value = slot
    isDeleteDialogOpen.value = true
  }

  const confirmDelete = () => {
    if (deletingSlot.value === -1) return

    emit('delete', deletingSlot.value)
    isDeleteDialogOpen.value = false
    deletingSlot.value = -1
  }

  /**
   * ==================================
   * 滑动切换屏显
   * ==================================
   */

  const setDragOffset = (offset: number) => {
    dragOffset.value = offset
  }

  const endDrag = (threshold = 100) => {
    if (dragOffset.value > threshold) {
      navigate(-1)
    } else if (dragOffset.value < -threshold) {
      navigate(1)
    }

    dragOffset.value = 0
  }

  const onMouseDown = (e: MouseEvent) => {
    if (!canNavigate.value) return

    cleanupMouseDrag()
    isDragging.value = true
    hasDragged.value = false
    startX.value = e.clientX
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    document.addEventListener('dragend', onMouseUp)
  }

  const onMouseMove = (e: MouseEvent) => {
    if (!isDragging.value) return

    e.preventDefault()
    const diff = e.clientX - startX.value
    if (Math.abs(diff) > 5) hasDragged.value = true
    setDragOffset(diff)
  }

  const onMouseUp = () => {
    isDragging.value = false
    endDrag(100)
    cleanupMouseDrag()
  }

  const cleanupMouseDrag = () => {
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
    document.removeEventListener('dragend', onMouseUp)
  }

  const onTouchStart = (e: TouchEvent) => {
    if (!canNavigate.value) return

    cleanupTouchDrag()
    isDragging.value = true
    hasDragged.value = false
    startX.value = e.touches[0].clientX
    document.addEventListener('touchmove', onTouchMove, { passive: false })
    document.addEventListener('touchend', onTouchEnd)
    document.addEventListener('touchcancel', onTouchEnd)
  }

  const onTouchMove = (e: TouchEvent) => {
    if (!isDragging.value) return

    e.preventDefault()
    const diff = e.touches[0].clientX - startX.value
    if (Math.abs(diff) > 5) hasDragged.value = true
    setDragOffset(diff)
  }

  const onTouchEnd = () => {
    isDragging.value = false
    endDrag(50)
    cleanupTouchDrag()
  }

  const cleanupTouchDrag = () => {
    document.removeEventListener('touchmove', onTouchMove)
    document.removeEventListener('touchend', onTouchEnd)
    document.removeEventListener('touchcancel', onTouchEnd)
  }

  watch(screenDisplays, (items) => {
    if (items.length === 0) return

    if (!items.some((item) => item.slot === props.page)) {
      emit('update:page', items[0].slot)
    }
  })

  onUnmounted(() => {
    cleanupMouseDrag()
    cleanupTouchDrag()
  })
</script>

<style scoped>
  .screen-carousel-reflection {
    -webkit-box-reflect: below 2px
      linear-gradient(
        to bottom,
        transparent 50%,
        rgba(0, 0, 0, 0.1) 66.6666%,
        rgba(0, 0, 0, 0.2) 83.3333%,
        rgba(0, 0, 0, 0.4) 100%
      );
  }
</style>
